/** @jsx jsx */
import { useEffect } from "react";
import BaseInput from "../Input";
import StepContainer from "../StepContainer";
import Divider from "@material-ui/core/Divider";
import { jsx, css } from "@emotion/core";
import InputMask from "react-input-mask";
import { Flex } from "rebass";
import { useMachine } from "@xstate/react";
import { getStep2 } from "./machine";
import Button from "../Button";
import { State } from "xstate";

const errors = {
  companyName: {
    empty: "Legal Company Name is a required field",
  },
  streetAddress: {
    empty: "Street address is a required field",
  },
  city: {
    empty: "City is a required field",
  },
  state: {
    empty: "State is a required field",
  },
  postalCode: {
    empty: "Zip code is a required field",
  },
  title: {
    empty: "Title is a required field",
  },
  companyPhoneNumber: {
    empty: "Company phone number is a required field",
    badFormat: "Please enter a valid phone number",
  },
};

function getErrorMessage(
  item: string,
  current: State<
    {
      companyName: string;
      streetAddress: string;
      streetAddress2?: string;
      city: string;
      state: string;
      postalCode: number;
      title: string;
      companyPhoneNumber: number;
    },
    any
  >
): string | undefined {
  const keys = errors[item] ? Object.keys(errors[item]) : [];
  let message = undefined;

  keys.some((key) => {
    if (current.matches(`ready.${item}.error.${key}`)) {
      message = errors[item][key];
    }
  });
  return message;
}

const Input = ({ current, ...props }) => (
  <BaseInput
    {...props}
    helperText={getErrorMessage(props.name, current)}
    css={css`
      margin-bottom: 20px;
    `}
  />
);

function Step2({
  companyName,
  streetAddress,
  streetAddress2,
  city,
  state,
  postalCode,
  companyPhoneNumber,
  title,
  onSubmit,
  onBack,
}: {
  companyName?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  postalCode?: number;
  companyPhoneNumber?: string;
  title?: string;
  onSubmit: (values: any) => void;
  onBack: (values: any) => void;
}) {
  const [current, send] = useMachine(
    getStep2({
      companyName,
      streetAddress,
      streetAddress2,
      city,
      state,
      postalCode,
      companyPhoneNumber,
      title,
    })
  );

  const finished = current.matches("finished");

  useEffect(() => {
    finished && onSubmit(current.context);
  }, [finished, current.context, onSubmit]);

  const handlePhoneChange = () => {};

  const handleChange: any = (type: string, property: string) => (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    send({
      type,
      [property]: e.currentTarget ? e.currentTarget.value : "",
    });
  };

  const handleCompanyNameChange = handleChange(
    "INPUT_COMPANY_NAME",
    "companyName"
  );
  const handleStreetAddressChange = handleChange(
    "INPUT_STREET_ADDRESS",
    "streetAddress"
  );
  const handleStreetAddress2Change = handleChange(
    "INPUT_STREET_ADDRESS2",
    "streetAddress2"
  );
  const handleCityChange = handleChange("INPUT_CITY", "city");
  const handleStateChange = handleChange("INPUT_STATE", "state");
  const handlePostalCodeChange = handleChange(
    "INPUT_POSTAL_CODE",
    "postalCode"
  );
  const handleCompanyPhoneChange = handleChange(
    "INPUT_COMPANY_PHONE",
    "companyPhoneNumber"
  );
  const handleTitleChange = handleChange("INPUT_TITLE", "title");

  return (
    <StepContainer title="Step 2 / 5: Company Information">
      <Input
        required
        error={current.matches("ready.companyName.error")}
        label="Legal Company Name"
        name="companyName"
        value={companyName}
        current={current}
        onChange={handleCompanyNameChange}
      />
      <Divider />
      <Input
        required
        error={current.matches("ready.streetAddress.error")}
        value={streetAddress}
        label="Street Address"
        name="streetAddress"
        current={current}
        onChange={handleStreetAddressChange}
      />
      <Input
        error={current.matches("ready.streetAddress2.error")}
        value={streetAddress2}
        label="Street Address Line 2"
        name="streetAddress2"
        current={current}
        onChange={handleStreetAddress2Change}
      />
      <Input
        required
        error={current.matches("ready.city.error")}
        value={city}
        label="City"
        name="city"
        current={current}
        onChange={handleCityChange}
      />
      <Input
        required
        error={current.matches("ready.state.error")}
        value={state}
        label="State"
        name="state"
        current={current}
        onChange={handleStateChange}
      />
      <Input
        required
        error={current.matches("ready.postalCode.error")}
        value={postalCode}
        label="Zip"
        name="postalCode"
        onChange={handlePostalCodeChange}
        current={current}
      />
      <Divider />
      <InputMask
        mask="(999)999-9999"
        value={companyPhoneNumber}
        onChange={handleCompanyPhoneChange}
      >
        {(inputProps) => (
          <Input
            required
            error={current.matches("ready.companyPhoneNumber.error")}
            label="Company Phone Number"
            current={current}
            name="companyPhoneNumber"
          />
        )}
      </InputMask>
      <Input
        required
        error={current.matches("ready.title.error")}
        value={title}
        label="Your title"
        name="title"
        current={current}
        onChange={handleTitleChange}
      />
      <Flex justifyContent="space-between">
        <Button onClick={() => onBack(current.context)} width={200}>
          Back
        </Button>

        <Button
          onClick={() => {
            send("SUBMIT");
          }}
          width={200}
          color="primary"
        >
          Continue
        </Button>
      </Flex>
    </StepContainer>
  );
}

export default Step2;
